'use client';

import React, { useState } from 'react';
import clsx from 'clsx';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import { StarIcon as StarIconOutline } from '@heroicons/react/24/outline';
import { STAR_RATING_MAX_STARS } from './star-rating-types';

const STARS = Array.from({ length: STAR_RATING_MAX_STARS }, (_, index) => index + 1);

interface StarRatingStarsProps {
  // `idPrefix` and `name` are per tile instance: two tiles on one page must not share element ids
  // or a radio group.
  idPrefix: string;
  name: string;
  selectedRating: number;
  legend: string;
  errorMessage?: string;
  getStarLabel: (rating: number) => string;
  onSelect: (rating: number) => void;
  onBlur: () => void;
  inputRef: React.Ref<HTMLInputElement>;
}

export function StarRatingStars({
  idPrefix,
  name,
  selectedRating,
  legend,
  errorMessage,
  getStarLabel,
  onSelect,
  onBlur,
  inputRef,
}: Readonly<StarRatingStarsProps>) {
  const [hoveredRating, setHoveredRating] = useState(0);

  const highlightedUpTo = hoveredRating || selectedRating;
  const errorId = `${idPrefix}-star-rating-error`;

  return (
    <fieldset onMouseLeave={() => setHoveredRating(0)}>
      <legend className="sr-only">{legend}</legend>
      <div className="flex items-center gap-1 sm:gap-2">
        {STARS.map((rating) => {
          const isHighlighted = rating <= highlightedUpTo;
          const StarIcon = isHighlighted ? StarIconSolid : StarIconOutline;
          const inputId = `${idPrefix}-star-${rating}`;

          return (
            <label
              key={rating}
              htmlFor={inputId}
              className="cursor-pointer p-1"
              onMouseEnter={() => setHoveredRating(rating)}
            >
              <input
                // The whole group is one form field, so only the first radio carries the ref that
                // react-hook-form focuses when validation fails.
                ref={rating === 1 ? inputRef : undefined}
                id={inputId}
                name={name}
                type="radio"
                value={rating}
                checked={selectedRating === rating}
                onChange={() => onSelect(rating)}
                onBlur={onBlur}
                className="peer sr-only"
                aria-label={getStarLabel(rating)}
                aria-describedby={errorMessage ? errorId : undefined}
              />
              <StarIcon
                aria-hidden="true"
                className={clsx(
                  'h-9 w-9 transition-colors duration-150 sm:h-11 sm:w-11',
                  'peer-focus-visible:outline peer-focus-visible:outline-2',
                  'peer-focus-visible:outline-offset-2 peer-focus-visible:outline-[#406AFF]',
                  isHighlighted ? 'text-primary-100' : 'text-neutral-50',
                )}
              />
            </label>
          );
        })}
      </div>
      {errorMessage && (
        <p id={errorId} className="mt-2 text-sm text-red">
          {errorMessage}
        </p>
      )}
    </fieldset>
  );
}
